<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { ddlHelper, sessionHelper } from "@/utils";
import { SESSION } from "@/constants";
import { required, email, sameAs } from "vuelidate/lib/validators";
import moment from "moment";
import { mapState } from "vuex";
import i18n from "@/i18n";
import { USER_MODULE } from '@/state/modules/user'
import { LOOKUP_MODULE } from '@/state/modules/lookup'
import { lookupMethods, userMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";

const checkFalse = (value) => value != false;

const checkPassword = (value) => {
  if (value != null && value != "") {
    return /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&])(?=.{8,20})/.test(
      value
    );
  } else {
    return true;
  }
};

export default {
  page: {
    title: i18n.t("label.updateFormX", [i18n.t("module.user")]),
  },
  components: { Layout, PageHeader, Multiselect },
  beforeRouteLeave(to, from, next) {
    if (to.name != "User-listing") {
      sessionHelper.clearSearch(SESSION.USER_SEARCH);
    }
    next();
  },
  computed: {
    ...mapState(USER_MODULE, ["status"]),
    ...mapState(LOOKUP_MODULE),
    roleDdl() {
      return this.$store.state.lookup.roleDdl;
    },
    data() {
      return this.$store.state.user.details;
    },
    selectedRole: {
      get() {
        return this.roleDdl.find((role) => role.value === this.data.roleId) || null;
      },
      set(role) {
        this.data.roleId = role ? role.value : null;
      },
    },
    selectedStatus: {
      get() {
        return this.statusDdl.find((status) => status.value == this.data.isActive) || null;
      },
      set(status) {
        this.data.isActive = status ? status.value : null;
      },
    },
    selectedCountries: {
      get() {
        let codes = this.data.countryCodes || [];
        return this.countryDdl.filter((c) => codes.includes(c.value));
      },
      set(options) {
        this.data.countryCodes = (options || []).map((o) => o.value);
      },
    },
    date() {
      return moment(this.$store.state.user.details.createdAt).format(
        "MMMM Do YYYY"
      );
    },
  },
  data() {
    return {
      title: i18n.t("label.updateFormX", [i18n.t("module.user")]),
      items: [
        {
          text: i18n.t("module.dashboard"),
          href: "/",
        },
        {
          text: i18n.t("module.user"),
          href: "/user",
        },
        {
          text: i18n.t("label.updateX", [i18n.t("module.user")]),
          active: true,
        },
      ],
      submitted: false,
      userPassword: "",
      passwordConfirmation: "",
      permissions: sessionHelper.getPermissions(),
      updatePermission: null,
      readPermission: null, 
      statusDdl: ddlHelper.getActivationStatus(false, true),
      countryDdl: ddlHelper.getAllMonitorCountries(),
    };
  },
  validations: {
    data: {
      name: { required },
      email: { required, email },
      isActive: { required },
      roleId: { checkFalse },
    },
    userPassword: { checkPassword },
    passwordConfirmation: { sameAs: sameAs("userPassword") },
  },
  async created() {
    this.searchCriteria = sessionHelper.retrieveSearch(SESSION.USER_SEARCH);
    await this.validPermissions();
    this.getRoleDdl();
    this.getUserDetails();
  },
  methods: {
    ...userMethods,
    ...lookupMethods,
    async validPermissions() {
      var jsonPermissions = JSON.parse(this.permissions)
      if (jsonPermissions.some(p => p.code === "USER_UPDATE")) {
        this.updatePermission = true;
      }
      else {
        this.updatePermission = false;
      }

      if(jsonPermissions.some(p => p.code === "USER_READ")) {
       this.readPermission = true; 
      }
      else {
        this.readPermission = false; 
      }
    },
    getRoleDdl() {
      this.lookupRole({ hasOptionAll: false });
    },
    getUserDetails() {
      if (this.readPermission == true) {
        this.getUser({ id: this.searchCriteria.selectedRecordId, });
      }
    },
    requestUpdate() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        let data = {
          id: this.data.id,
          name: this.data.name,
          email: this.data.email,
          roleId: this.data.roleId,
          countryCodes: this.data.countryCodes || [],
          password: this.userPassword,
        };
        if (this.updatePermission == true) {
          this.updateUser({ data });
        }
      }
    },
    requestUnlock() {
      let id = this.data.id;
      this.unlockUser({ id });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="requestUpdate" v-if="readPermission == true">
              <b-form-group id="name" label-cols-sm="2" label-cols-lg="2" :label="$t('label.name')" label-for="name">
                <b-form-input for="name" v-model.trim="data.name"
                  :class="{ 'is-invalid': !$v.data.name.required }"></b-form-input>
                <div v-if="!$v.data.name.required" class="invalid-feedback">
                  <span v-if="!$v.data.name.required">{{
                    $t("message.requiredX", [$t("label.name")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="email" label-cols-sm="2" label-cols-lg="2" :label="$t('label.email')" label-for="email">
                <b-form-input id="email" v-model.trim="data.email" :class="{
                  'is-invalid':
                    !$v.data.email.required || !$v.data.email.email,
                }"></b-form-input>
                <div v-if="!$v.data.email.required || !$v.data.email.email" class="invalid-feedback">
                  <span v-if="!$v.data.email.required">{{
                    $t("message.requiredX", [$t("label.email")])
                  }}</span>
                  <span v-if="!$v.data.email.email">{{
                    $t("message.validX", [$t("label.email")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="isLocked" label-cols-sm="2" label-cols-lg="2" :label="$t('label.accountStatus')"
                label-for="isLocked">
                <b-form-input id="isLocked" :value="data.isLocked === true ? $t('label.lock') : $t('label.active')"
                  disabled></b-form-input>
              </b-form-group>

              <b-form-group id="createdAt" label-cols-sm="2" label-cols-lg="2" :label="$t('label.createdAt')"
                label-for="createdAt">
                <b-form-input id="createdAt" v-model="date" type="datetime" disabled></b-form-input>
              </b-form-group>

              <div class="form-group row mb-0" style="padding-bottom: 10px">
                <label class="col-md-2 col-form-label">{{ $t('label.userStatus') }}</label>
                <div class="col-md-10">
                  <multiselect v-model="selectedStatus" :options="statusDdl" label="text" track-by="value"
                    :show-labels="false" :allow-empty="false"
                    :placeholder="$t('message.pleaseSelectX', [$t('label.userStatus')])"></multiselect>
                </div>
              </div>

              <div class="form-group row mb-0">
                <label class="col-md-2 col-form-label">{{ $t('module.role') }}</label>
                <div class="col-md-10">
                  <multiselect v-model="selectedRole" :options="roleDdl" label="text" track-by="value"
                    :show-labels="false" :allow-empty="false"
                    :placeholder="$t('message.pleaseSelectX', [$t('module.role')])"></multiselect>
                  <div v-if="!$v.data.roleId.checkFalse" style="color: #ff3d60; font-size: 80%">
                    <span>{{ $t("message.requiredX", [$t("module.role")]) }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group row mb-0" style="padding-bottom: 10px">
                <label class="col-md-2 col-form-label">{{ $t('label.allowedCountries') }}</label>
                <div class="col-md-10">
                  <multiselect v-model="selectedCountries" :options="countryDdl" label="text" track-by="value"
                    :multiple="true" :close-on-select="false" :show-labels="false"
                    :placeholder="$t('label.allCountries')"></multiselect>
                  <small class="form-text text-muted">{{ $t('message.allowedCountriesHelp') }}</small>
                </div>
              </div>

              <b-form-group id="password" label-cols-sm="2" label-cols-lg="2" :label="$t('label.password')"
                label-for="password">
                <b-form-input id="password" type="password" v-model.trim="userPassword" autocomplete="new-password"
                  :placeholder="$t('label.enter', [$t('label.password')])"
                  :class="{ 'is-invalid': !$v.userPassword.checkPassword }"></b-form-input>
                <div v-if="!$v.userPassword.checkPassword" class="invalid-feedback">
                  <span>{{ $t("message.passwordErrorMessage") }}</span>
                </div>
              </b-form-group>

              <b-form-group id="passwordConfirmation" label-cols-sm="2" label-cols-lg="2"
                :label="$t('label.confirmationPassword')" label-for="passwordConfirmation">
                <b-form-input id="passwordConfirmation" type="password" v-model.trim="passwordConfirmation"
                  autocomplete="new-password" :placeholder="$t('label.enter', [$t('label.confirmationPassword')])"
                  :class="{ 'is-invalid': !$v.passwordConfirmation.sameAs }"></b-form-input>
                <div v-if="!$v.passwordConfirmation.sameAs" class="invalid-feedback">
                  <span>{{ $t("message.sameAs") }}</span>
                </div>
              </b-form-group>

              <div class="mt-4 text-center" style="float: right">
                <button class="btn btn-info w-md waves-effect waves-light" style="margin-right: 10px" type="submit"
                  :disabled="status.update || updatePermission != true">
                  {{ $t("label.updateX", [$t('module.user')]) }}
                </button>

                <button class="btn btn-info w-md waves-effect waves-light" type="button" @click="requestUnlock"
                  :disabled="status.update">
                  {{ $t('label.unlockAccount') }}
                </button>
              </div>
            </form>
            <div style="text-align: center;" v-else>
              {{ $t('message.noPermissionMessage') }}
            </div>
          </div>
        </div>
        <!-- end card -->
      </div>
      <!-- end col -->
    </div>
  </Layout>
</template>